import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import { isNil, isFunction } from 'lodash'
import {
  useFloating,
  offset,
  flip,
  shift,
  inline,
  autoUpdate,
  useClick,
  useHover,
  useInteractions,
  useFocus,
  useDismiss,
  useRole,
  safePolygon,
  arrow,
  FloatingArrow,
  useTransitionStyles,
} from '@floating-ui/react'
import { PopoverWrapper } from './PopoverWrapper'

import './InteractiveTooltip.scss'

const getThemeMap = (finalPlacement) => ({
  dark: {
    fill: 'var(--color-background-inverse)',
    stroke: 'var(--color-border)',
  },
  light: {
    fill: 'var(--color-background-surface)',
    stroke: 'var(--color-border)',
  },
  magical: {
    fill:
      finalPlacement === 'top'
        ? 'var(--color-background-magical)'
        : 'var(--color-background-surface)',
    stroke: 'var(--color-border-magical)',
  },
})

/**
 * Floating UI - Interactive Tooltip
 * @docs https://floating-ui.com/docs/getting-started
 * @import import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
 * @param {object} props
 * @param {node} props.children Trigger element the tooltip is anchored to
 * @param {node} props.content Tooltip content
 * @param {'top'|'top-start'|'top-end'|'right'|'right-start'|'right-end'|'bottom'|'bottom-start'|'bottom-end'|'left'|'left-start'|'left-end'} [props.placement] Preferred side; flips when there is no room
 * @param {boolean} [props.hide] Disables the tooltip and closes it if open
 * @param {'dark'|'light'|'magical'} [props.theme] Color scheme
 * @param {'small'|'large'} [props.size] large adds padding for rich content
 * @param {boolean} [props.clickable] Click-to-open popover instead of a hover tooltip
 * @param {boolean} [props.interactive] Lets the cursor move into the tooltip (for selecting/copying its content)
 * @param {boolean} [props.narrow] Caps width at 250px (vs the default 350px) for short notes that look sparse
 * @param {string} [props.className] Additional classes on the tooltip body
 * @param {boolean} [props.open] Controlled open state; pair with setOpen
 * @param {Function} [props.setOpen] Controlled open state setter; pair with open
 * @param {boolean} [props.block] Block-level trigger so the tooltip anchors to the full width
 */
export const InteractiveTooltip = ({
  children,
  content,
  placement = 'top',
  hide = false,
  theme = 'dark',
  size = 'small',
  clickable = false, // clickable === isPopover
  interactive = false,
  narrow = false,
  className,
  open,
  setOpen,
  block = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const isExternallyManaged = !isNil(open) && isFunction(setOpen)
  const resolvedOpen = isExternallyManaged ? open : isOpen
  const resolvedSetOpen = isExternallyManaged ? setOpen : setIsOpen

  // Hiding must also reset the open state. Hover interactions are disabled
  // while hidden, so a tooltip left open when `hide` flipped on would never
  // receive its mouseleave and would reappear, stuck, once `hide` turns off.
  useEffect(() => {
    if (hide && resolvedOpen) {
      resolvedSetOpen(false)
    }
  }, [hide, resolvedOpen, resolvedSetOpen])

  const {
    refs,
    floatingStyles,
    context,
    placement: finalPlacement,
  } = useFloating({
    open: hide ? false : resolvedOpen,
    onOpenChange: resolvedSetOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(9),
      inline(),
      flip(),
      shift({ padding: 4 }),
      arrow({ element: arrowRef, padding: 12 }), // ensures the arrow does not overflow the floating element
    ],
    placement,
  })

  const hover = useHover(context, {
    enabled: !hide && !clickable,
    move: false,
    handleClose: interactive ? safePolygon({ buffer: 1, blockPointerEvents: true }) : undefined,
  })

  const click = useClick(context, { enabled: !hide && clickable })
  const focus = useFocus(context, { enabled: !hide && !clickable })
  const dismiss = useDismiss(context, { enabled: !hide, outsidePress: clickable, escapeKey: true })
  const role = clickable
    ? useRole(context, { role: 'dialog' })
    : useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ])
  // Use isMounted for open/close state if transitionStyles are used
  // note: transitionStyles and floatingStyles cannot be used on the same element, which is why it's nested
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: { open: 200, close: 100 },
    initial: { opacity: 0, transform: 'translateY(4px)' },
    open: { opacity: 1, transform: 'translateY(0)' },
    close: { opacity: 0, transform: 'translateY(4px)' },
  })

  const themeMap = getThemeMap(finalPlacement)

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps({
          // Popovers are often rendered inside clickable parents (collapsible headers,
          // editable rows); stop the toggle click from bubbling into those handlers.
          onClick: clickable ? (event) => event.stopPropagation() : undefined,
        })}
        className={classNames('interactive-tooltip-trigger', {
          'is-block': block,
          'is-clickable': clickable,
        })}
      >
        {children}
      </span>

      {isMounted && (
        <PopoverWrapper context={context} isPopover={clickable}>
          <div
            ref={refs.setFloating}
            className="interactive-tooltip-container"
            style={floatingStyles}
            {...getFloatingProps()}
            onClickCapture={undefined}
            onMouseDownCapture={undefined}
            onPointerDownCapture={undefined}
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            <div
              style={transitionStyles}
              className={classNames(
                `interactive-tooltip interactive-tooltip__${theme} interactive-tooltip__${size}`,
                { 'interactive-tooltip__narrow': narrow },
                className
              )}
            >
              <FloatingArrow
                ref={arrowRef}
                context={context}
                fill={themeMap[theme].fill}
                stroke={themeMap[theme].stroke}
                strokeWidth={1}
              />
              {content}
            </div>
          </div>
        </PopoverWrapper>
      )}
    </>
  )
}
